import { useState } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import PhoneInput from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import 'react-phone-number-input/style.css';
import { UserPlus } from 'lucide-react';
import {
  createShopPlayer,
} from '../services/api';
import type { ShopPlayer } from '../types';
import { isPlausibleInternationalPhone } from '../utils/phone';

export type ShopPlayerLinkFlow = 'create';

type Props = {
  /** After create completes, parent refetches the list */
  onLinked: () => void;
  /** Optional: return linked player id/phone for immediate selection */
  onLinkedPlayer?: (player: { id: string; phone: string; player?: ShopPlayer }) => void;
  /** Optional: focus table search on this phone when already in shop */
  onInShop?: (phone: string) => void;
  /** When false, hide the inner title strip (e.g. modal already has a title) */
  showPanelHeader?: boolean;
};

export function ShopPlayerLinkByPhonePanel({
  onLinked,
  onLinkedPlayer,
  onInShop,
  showPanelHeader = true,
}: Props) {
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');

  const resetForm = () => {
    setPhone('');
    setEmail('');
  };

  const handlePhoneChange = (v: string | undefined) => {
    setPhone(v ?? '');
  };

  const onCreate = async () => {
    const phoneTrim = phone.trim();
    const emailTrim = email.trim();
    const hasPhone = phoneTrim.length > 0;
    const hasEmail = emailTrim.length > 0;
    if (!hasPhone && !hasEmail) {
      toast.error('Enter a phone number or email');
      return;
    }
    if (hasPhone && !isPlausibleInternationalPhone(phoneTrim)) {
      toast.error('Enter a phone number with country code (e.g. +1, +84)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createShopPlayer({
        phone: phoneTrim,
        ...(hasEmail ? { email: emailTrim } : {}),
      });

      if (res.success && res.data && typeof res.data === 'object' && 'id' in res.data) {
        const player = res.data as ShopPlayer;
        toast.success('Player created successfully.');
        onLinkedPlayer?.({ id: player.id, phone: phone.trim(), player });
        resetForm();
        onLinked();
        return;
      }

      if (!res.success && res.code === 'ALREADY_YOUR_PLAYER') {
        toast.error(res.message || 'Already your Player');
        if (phone.trim()) onInShop?.(phone.trim());
        return;
      }

      toast.error(res.message || 'Could not create Player');
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) && err.response?.data && typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : 'Could not create Player';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };
  const panelTitle = 'Create Player';
  const panelSubtitle = 'Create a new Player only. Existing Players can’t be added/linked to your Shop.';

  return (
    <div className="overflow-hidden rounded-xl border border-emerald-100 bg-white shadow-sm">
      {showPanelHeader && (
        <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-3">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-700" aria-hidden />
            <h2 className="text-sm font-semibold text-gray-900">{panelTitle}</h2>
          </div>
          <p className="mt-1 text-xs text-gray-500">{panelSubtitle}</p>
        </div>
      )}

      <div className="p-4">
        <label htmlFor="link-player-phone" className="block text-sm font-medium text-gray-700">
          Phone number
        </label>
        <div className="transfer-phone-field mt-1.5">
          <div className="min-w-0 flex-1">
            <PhoneInput
              id="link-player-phone"
              international
              defaultCountry="US"
              countryCallingCodeEditable={false}
              labels={en}
              placeholder="Enter phone number"
              value={phone || undefined}
              onChange={handlePhoneChange}
              className="PhoneInput"
              autoComplete="tel"
            />
          </div>
        </div>

        <div className="mt-3">
          <label htmlFor="link-player-email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            id="link-player-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
            placeholder="name@example.com"
          />
        </div>

        <div className="mt-3">
          <button
            type="button"
            disabled={submitting}
            onClick={() => void onCreate()}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-gray-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-900 disabled:opacity-60"
          >
            <UserPlus className="h-4 w-4" />
            {submitting ? 'Creating…' : 'Create Player'}
          </button>
        </div>
      </div>
    </div>
  );
}
